import { useMemo, useState } from 'react';
import Editor, { loader } from '@monaco-editor/react';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import type { CodeBuilderDraft } from './types';

loader.config({ paths: { vs: '/monaco/vs' } });

// Dev Mode is a read-only IDE-style view of the code the interview is currently
// proposing: the draft's `operations` shown as a file tree + Monaco viewer.
// It is self-contained - it does not need a Knowledge Builder session.
export const CodeBuilderDevModePanel = (props: { draft?: CodeBuilderDraft }) => {
  const operations = useMemo(
    () => [...(props.draft?.operations ?? [])].sort((a, b) => a.path.localeCompare(b.path)),
    [props.draft],
  );
  const [selectedPath, setSelectedPath] = useState<string>();

  const selected =
    operations.find(operation => operation.path === selectedPath) ??
    operations[0];

  if (!operations.length) {
    return (
      <div style={{ padding: 16 }}>
        <Typography variant="body2" color="textSecondary">
          No draft yet. Run the interview until it proposes file changes, then
          review them here.
        </Typography>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      <div
        style={{
          width: 260,
          flexShrink: 0,
          borderRight: '1px solid rgba(128,128,128,0.3)',
          overflow: 'auto',
        }}
      >
        {operations.map(operation => {
          const isActive = operation.path === (selected?.path ?? '');
          return (
            <button
              key={operation.path}
              type="button"
              onClick={() => setSelectedPath(operation.path)}
              title={operation.explanation}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                width: '100%',
                textAlign: 'left',
                padding: '6px 10px',
                border: 0,
                background: isActive ? 'rgba(128,128,128,0.15)' : 'transparent',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              <Chip
                size="small"
                label={operation.op}
                style={{ height: 18, fontSize: 10 }}
              />
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {operation.path}
              </span>
            </button>
          );
        })}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {selected ? (
          <>
            <div
              style={{
                padding: '6px 12px',
                borderBottom: '1px solid rgba(128,128,128,0.3)',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                {selected.op} · {selected.explanation}
              </Typography>
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
              <Editor
                path={`rdap-code-builder://${selected.path}`}
                value={selected.content}
                language={languageForPath(selected.path)}
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                  fontSize: 12,
                  wordWrap: 'on',
                  scrollBeyondLastLine: false,
                }}
              />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

function languageForPath(path: string) {
  if (/\.tsx?$/i.test(path)) return 'typescript';
  if (/\.jsx?$/i.test(path)) return 'javascript';
  if (/\.json$/i.test(path)) return 'json';
  if (/\.ya?ml$/i.test(path)) return 'yaml';
  if (/\.md$/i.test(path)) return 'markdown';
  if (/\.css$/i.test(path)) return 'css';
  if (/\.html?$/i.test(path)) return 'html';
  if (/\.py$/i.test(path)) return 'python';
  if (/\.go$/i.test(path)) return 'go';
  if (/\.(sh|bash)$/i.test(path)) return 'shell';
  return 'plaintext';
}
